import { useState } from "react";
import { KeyRound, LayoutGrid, ListTree, Plus, Rocket, SquareStack, Unplug, Wrench } from "lucide-react";
import { useLocale, useTranslation } from "@ncp/i18n";
import { Badge, Button, Card, CardContent, Input, NativeSelect, Switch, Textarea, cn } from "@ncp/ui";
import type { PageSummary, SpaceHomeSection, SpaceManifest, SpaceMatterTab, SpaceNavEntry, SpaceTabGroup, SpaceUseCase } from "../../pages/api";
import type { BuilderRef, SpaceBuiltins } from "../api";
import { ListInput, PagePicker, RowActions, SectionTitle, TextPair, move, replace, swap, without } from "./controls";

export const NAV_SECTIONS = ["work", "insight", "resources"] as const;

export interface SectionProps {
  m: SpaceManifest;
  set: (patch: Partial<SpaceManifest>) => void;
  pages: PageSummary[] | null;
  readOnly: boolean;
}

// ---------------------------------------------------------------- home

export function HomeSection({ m, set, pages, readOnly, builtins }: SectionProps & { builtins: SpaceBuiltins | undefined }) {
  const { t } = useTranslation();
  const list = m.home;
  const put = (home: SpaceHomeSection[]) => set({ home });
  const add = (s: SpaceHomeSection) => put([...list, s]);
  return (
    <Card><CardContent className="pt-5">
      <SectionTitle icon={<LayoutGrid className="size-4 text-aegold-700" />} title={t("admin:spaceDesigner.home.title")} hint={t("admin:spaceDesigner.home.hint")}
        actions={readOnly ? null : (<>
          <Button size="sm" variant="outline" onClick={() => add({ kind: "page", page: "", span: 12 })}><Plus /> {t("admin:spaceDesigner.home.addPage")}</Button>
          <Button size="sm" variant="outline" onClick={() => add({ kind: "use-cases", span: 12 })}><Plus /> {t("admin:spaceDesigner.home.addUseCases")}</Button>
          {builtins?.home.length ? <Button size="sm" variant="outline" onClick={() => add({ kind: "builtin", key: builtins.home[0], span: 6 })}><Plus /> {t("admin:spaceDesigner.home.addWidget")}</Button> : null}
        </>)} />
      {list.length === 0 ? <p className="text-sm text-muted-foreground">{t("admin:spaceDesigner.home.empty")}</p> : (
        <ol className="space-y-2">
          {list.map((s, i) => (
            <li key={i} className="rounded-lg border border-sand-200 bg-white p-2.5">
              <div className="flex flex-wrap items-center gap-2">
                <span className="w-5 text-center text-xs font-semibold text-sand-600 tabular">{i + 1}</span>
                <NativeSelect value={s.kind} disabled={readOnly} className="h-8 w-40" onChange={(e) => put(replace(list, i, { kind: e.target.value, span: s.span ?? 12, title: s.title, ...(e.target.value === "builtin" ? { key: builtins?.home[0] } : e.target.value === "page" ? { page: "" } : {}) }))}>
                  <option value="page">{t("admin:spaceDesigner.home.kind.page")}</option>
                  <option value="use-cases">{t("admin:spaceDesigner.home.kind.use-cases")}</option>
                  <option value="builtin">{t("admin:spaceDesigner.home.kind.builtin")}</option>
                </NativeSelect>
                {s.kind === "builtin" ? (
                  <NativeSelect value={s.key ?? ""} disabled={readOnly} className="h-8 min-w-48 flex-1" onChange={(e) => put(replace(list, i, { ...s, key: e.target.value }))}>
                    {(builtins?.home ?? []).map((k) => <option key={k} value={k}>{t(`admin:spaceBuiltins.widget.${k}.name`, { defaultValue: k })}</option>)}
                  </NativeSelect>
                ) : s.kind === "page" ? (
                  <PagePicker value={s.page} pages={pages} slot="home" readOnly={readOnly} className="min-w-48 flex-1" onChange={(page) => put(replace(list, i, { ...s, page }))} />
                ) : <span className="flex-1 text-xs text-muted-foreground">{t("admin:spaceDesigner.home.useCasesNote", { count: m.useCases.length })}</span>}
                <NativeSelect value={String(s.span ?? 12)} disabled={readOnly} className="h-8 w-32" onChange={(e) => put(replace(list, i, { ...s, span: Number(e.target.value) }))} aria-label={t("admin:spaceDesigner.home.width")}>
                  <option value="12">{t("admin:spaceDesigner.home.full")}</option>
                  <option value="6">{t("admin:spaceDesigner.home.half")}</option>
                </NativeSelect>
                <RowActions index={i} count={list.length} disabled={readOnly} onMove={(by) => put(move(list, i, by))} onRemove={() => put(without(list, i))} />
              </div>
              {s.kind !== "builtin" ? <TextPair className="mt-2 ps-7" value={s.title} readOnly={readOnly} placeholder={t("admin:spaceDesigner.home.titlePlaceholder")} onChange={(title) => put(replace(list, i, { ...s, title }))} />
                : s.key ? <p className="mt-1.5 ps-7 text-xs text-muted-foreground">{t(`admin:spaceBuiltins.widget.${s.key}.hint`, { defaultValue: "" })}</p> : null}
            </li>
          ))}
        </ol>
      )}
    </CardContent></Card>
  );
}

// ---------------------------------------------------------------- navigation

export function NavSection({ m, set, pages, readOnly, builtins }: SectionProps & { builtins: SpaceBuiltins | undefined }) {
  const { t } = useTranslation();
  const list = m.nav;
  const put = (nav: SpaceNavEntry[]) => set({ nav });
  const unique = (base: string) => { let k = base || "entry"; let n = 2; while (list.some((e) => e.key === k)) k = `${base}-${n++}`; return k; };
  return (
    <Card><CardContent className="pt-5">
      <SectionTitle icon={<ListTree className="size-4 text-aegold-700" />} title={t("admin:spaceDesigner.nav.title")} hint={t("admin:spaceDesigner.nav.hint")} />
      <div className="grid gap-3 xl:grid-cols-3">
        {NAV_SECTIONS.map((section) => {
          const rows = list.map((e, i) => ({ e, i })).filter(({ e }) => e.section === section);
          return (
            <section key={section} className="rounded-xl border border-sand-200 bg-sand-50/40 p-2.5">
              <div className="mb-2 flex items-center justify-between">
                <span className="text-xs font-semibold uppercase tracking-wide text-sand-700">{t(`admin:spaceDesigner.nav.sections.${section}`)}</span>
                {!readOnly ? (
                  <span className="flex gap-1">
                    <Button size="sm" variant="ghost" onClick={() => put([...list, { key: unique("page"), page: "", section }])}><Plus /> {t("admin:spaceDesigner.nav.addPage")}</Button>
                    <Button size="sm" variant="ghost" onClick={() => put([...list, { key: unique("knowledge"), builtin: "knowledge", section }])}><Plus /> {t("admin:spaceDesigner.nav.addBuiltin")}</Button>
                  </span>
                ) : null}
              </div>
              {rows.length === 0 ? <p className="px-1 text-xs text-muted-foreground">{t("admin:spaceDesigner.nav.empty")}</p> : (
                <ul className="space-y-2">
                  {rows.map(({ e, i }, k) => (
                    <li key={e.key + i} className="rounded-lg border border-sand-200 bg-white p-2">
                      <div className="flex items-center gap-1.5">
                        {e.builtin !== undefined && e.builtin !== null ? (
                          <NativeSelect value={e.builtin} disabled={readOnly} className="h-8 flex-1" onChange={(ev) => put(replace(list, i, { ...e, builtin: ev.target.value, key: e.key.startsWith(e.builtin ?? "") ? unique(ev.target.value) : e.key }))}>
                            {(builtins?.nav ?? [e.builtin]).map((b) => <option key={b} value={b}>{t(`admin:spaceDesigner.builtin.${b}`, { defaultValue: b })}</option>)}
                          </NativeSelect>
                        ) : (
                          <PagePicker value={e.page} pages={pages} slot="nav" readOnly={readOnly} className="flex-1" onChange={(page) => put(replace(list, i, { ...e, page, key: !e.key || e.key.startsWith("page") ? unique(page) : e.key }))} />
                        )}
                        <RowActions index={k} count={rows.length} disabled={readOnly} onMove={(by) => { const target = rows[k + by]; if (target) put(swap(list, i, target.i)); }} onRemove={() => put(without(list, i))} />
                      </div>
                      <TextPair className="mt-1.5" value={e.label} readOnly={readOnly} placeholder={t("admin:spaceDesigner.nav.labelPlaceholder")} onChange={(label) => put(replace(list, i, { ...e, label }))} />
                      <div className="mt-1.5 grid grid-cols-2 gap-1.5">
                        <Input value={e.icon ?? ""} readOnly={readOnly} dir="ltr" placeholder={t("admin:spaceDesigner.nav.icon")} className="h-8 font-mono" onChange={(ev) => put(replace(list, i, { ...e, icon: ev.target.value || null }))} />
                        <NativeSelect value={e.section} disabled={readOnly} className="h-8" onChange={(ev) => put(replace(list, i, { ...e, section: ev.target.value }))} aria-label={t("admin:spaceDesigner.nav.section")}>
                          {NAV_SECTIONS.map((s) => <option key={s} value={s}>{t(`admin:spaceDesigner.nav.sections.${s}`)}</option>)}
                        </NativeSelect>
                        <Input value={e.group ?? ""} readOnly={readOnly} placeholder={t("admin:spaceDesigner.nav.group")} className="h-8" onChange={(ev) => put(replace(list, i, { ...e, group: ev.target.value || null }))} />
                        <Input value={e.permission ?? ""} readOnly={readOnly} dir="ltr" placeholder={t("admin:spaceDesigner.nav.permission")} className="h-8 font-mono" onChange={(ev) => put(replace(list, i, { ...e, permission: ev.target.value || null }))} />
                      </div>
                      <p className="mt-1 font-mono text-[10px] text-muted-foreground" dir="ltr">{e.key}</p>
                    </li>
                  ))}
                </ul>
              )}
            </section>
          );
        })}
      </div>
    </CardContent></Card>
  );
}

// ---------------------------------------------------------------- matter tabs by stage

export function MatterTabsSection({ m, set, pages, readOnly, playbooks, builtins }: SectionProps & { playbooks: BuilderRef[]; builtins: SpaceBuiltins | undefined }) {
  const { t } = useTranslation();
  const { locale } = useLocale();
  const putGroups = (playbook: string, groups: SpaceTabGroup[] | null) => {
    const next = { ...m.matterTabs };
    if (groups) next[playbook] = groups; else delete next[playbook];
    set({ matterTabs: next });
  };
  const tabs = builtins?.tabs ?? [];
  return (
    <Card><CardContent className="pt-5">
      <SectionTitle icon={<SquareStack className="size-4 text-aegold-700" />} title={t("admin:spaceDesigner.matters.title")} hint={t("admin:spaceDesigner.matters.hint")} />
      {playbooks.length === 0 ? <p className="text-sm text-muted-foreground">{t("admin:spaceBuiltins.noPlaybooks")}</p> : (
        <div className="space-y-3">
          {playbooks.map((pb) => {
            const groups = m.matterTabs[pb.key] ?? null;
            const own = builtins?.playbookTabs[pb.key] ?? [];
            return (
              <section key={pb.key} className="rounded-xl border border-sand-200 bg-white p-3">
                <div className="flex flex-wrap items-center gap-2">
                  <span className="min-w-0 flex-1"><span className="block text-sm font-medium">{(locale === "ar" && pb.nameAr) || pb.name}</span><span className="font-mono text-[11px] text-muted-foreground">{pb.key}</span></span>
                  <label className="flex items-center gap-2 text-xs text-sand-700">
                    <Switch checked={!!groups} disabled={readOnly} onCheckedChange={(on) => putGroups(pb.key, on ? [{ stage: "all", label: null, tabs: own.map((b) => ({ builtin: b })) }] : null)} /> {t("admin:spaceDesigner.matters.customise")}
                  </label>
                </div>
                {!groups ? (
                  <div className="mt-2 flex flex-wrap items-center gap-1.5">
                    <span className="text-[11px] text-muted-foreground">{t("admin:spaceBuiltins.playbookDefault")}:</span>
                    {own.length === 0 ? <span className="text-[11px] text-muted-foreground">{t("admin:spaceBuiltins.playbookNone")}</span> : own.map((k) => <Badge key={k} variant="outline" size="sm">{t(`admin:spaceBuiltins.tab.${k}`, { defaultValue: k })}</Badge>)}
                  </div>
                ) : (
                  <div className="mt-3 space-y-2">
                    {groups.map((g, gi) => (
                      <div key={gi} className="rounded-lg border border-sand-200 bg-sand-50/50 p-2.5">
                        <div className="flex flex-wrap items-center gap-1.5">
                          <Input value={g.stage} readOnly={readOnly} dir="ltr" className="h-8 w-36 font-mono" placeholder={t("admin:spaceDesigner.matters.stage")} onChange={(e) => putGroups(pb.key, replace(groups, gi, { ...g, stage: e.target.value }))} />
                          <TextPair className="min-w-64 flex-1" value={g.label} readOnly={readOnly} placeholder={t("admin:spaceDesigner.matters.stageLabel")} onChange={(label) => putGroups(pb.key, replace(groups, gi, { ...g, label }))} />
                          <RowActions index={gi} count={groups.length} disabled={readOnly} onMove={(by) => putGroups(pb.key, move(groups, gi, by))} onRemove={() => putGroups(pb.key, without(groups, gi))} />
                        </div>
                        <ListInput className="mt-1.5" value={g.states} readOnly={readOnly} placeholder={t("admin:spaceDesigner.matters.states")} onChange={(states) => putGroups(pb.key, replace(groups, gi, { ...g, states: states.length ? states : null }))} />
                        <ul className="mt-2 space-y-1.5">
                          {g.tabs.map((tab, ti) => (
                            <li key={ti} className="flex flex-wrap items-center gap-1.5">
                              <TabTarget tab={tab} builtins={tabs} pages={pages} readOnly={readOnly} onChange={(next) => putGroups(pb.key, replace(groups, gi, { ...g, tabs: replace(g.tabs, ti, next) }))} />
                              <TextPair className="min-w-56 flex-1" value={tab.label} readOnly={readOnly} placeholder={t("admin:spaceDesigner.matters.tabLabel")} onChange={(label) => putGroups(pb.key, replace(groups, gi, { ...g, tabs: replace(g.tabs, ti, { ...tab, label }) }))} />
                              <Input className="h-8 w-44" value={tab.when ?? ""} readOnly={readOnly} placeholder={t("admin:spaceDesigner.matters.when")} title={t("admin:spaceDesigner.matters.whenHint")}
                                onChange={(e) => putGroups(pb.key, replace(groups, gi, { ...g, tabs: replace(g.tabs, ti, { ...tab, when: e.target.value.trim() || null }) }))} />
                              <RowActions index={ti} count={g.tabs.length} disabled={readOnly} onMove={(by) => putGroups(pb.key, replace(groups, gi, { ...g, tabs: move(g.tabs, ti, by) }))} onRemove={() => putGroups(pb.key, replace(groups, gi, { ...g, tabs: without(g.tabs, ti) }))} />
                            </li>
                          ))}
                        </ul>
                        {!readOnly ? (
                          <div className="mt-2 flex gap-1">
                            <Button size="sm" variant="ghost" onClick={() => putGroups(pb.key, replace(groups, gi, { ...g, tabs: [...g.tabs, { page: "" }] }))}><Plus /> {t("admin:spaceDesigner.matters.addPageTab")}</Button>
                            {tabs.length ? <Button size="sm" variant="ghost" onClick={() => putGroups(pb.key, replace(groups, gi, { ...g, tabs: [...g.tabs, { builtin: tabs[0] }] }))}><Plus /> {t("admin:spaceDesigner.matters.addBuiltinTab")}</Button> : null}
                          </div>
                        ) : null}
                      </div>
                    ))}
                    {!readOnly ? <Button size="sm" variant="outline" onClick={() => putGroups(pb.key, [...groups, { stage: `stage-${groups.length + 1}`, label: null, tabs: [] }])}><Plus /> {t("admin:spaceDesigner.matters.addStage")}</Button> : null}
                  </div>
                )}
              </section>
            );
          })}
        </div>
      )}
    </CardContent></Card>
  );
}

function TabTarget({ tab, builtins, pages, readOnly, onChange }: { tab: SpaceMatterTab; builtins: string[]; pages: PageSummary[] | null; readOnly: boolean; onChange: (t: SpaceMatterTab) => void }) {
  const { t } = useTranslation();
  if (tab.builtin) {
    return (
      <NativeSelect value={tab.builtin} disabled={readOnly} className="h-8 w-48" onChange={(e) => onChange({ ...tab, builtin: e.target.value })}>
        {builtins.map((b) => <option key={b} value={b}>{t(`admin:spaceBuiltins.tab.${b}`, { defaultValue: b })}</option>)}
      </NativeSelect>
    );
  }
  return <PagePicker value={tab.page} pages={pages} slot="work-tab" readOnly={readOnly} className="w-56" onChange={(page) => onChange({ ...tab, page })} />;
}

// ---------------------------------------------------------------- workbench and mounts

export function PagesListSection({ m, set, pages, readOnly, field }: SectionProps & { field: "workbench" | "mounts" }) {
  const { t } = useTranslation();
  const list = m[field];
  const put = (v: string[]) => set({ [field]: v } as Partial<SpaceManifest>);
  const [adding, setAdding] = useState("");
  const Icon = field === "workbench" ? Wrench : Unplug;
  return (
    <Card><CardContent className="pt-5">
      <SectionTitle icon={<Icon className="size-4 text-aegold-700" />} title={t(`admin:spaceDesigner.${field}.title`)} hint={t(`admin:spaceDesigner.${field}.hint`)} />
      <div className="flex flex-wrap gap-1.5">
        {list.length === 0 ? <span className="text-sm text-muted-foreground">{t(`admin:spaceDesigner.${field}.empty`)}</span> : list.map((k, i) => (
          <Badge key={k} variant="outline" className="gap-1">
            <code dir="ltr">{k}</code>
            {!readOnly ? <button type="button" aria-label={t("admin:common.remove")} onClick={() => put(without(list, i))} className="text-sand-500 hover:text-danger-600">×</button> : null}
          </Badge>
        ))}
      </div>
      {!readOnly ? (
        <div className="mt-2 flex gap-1.5">
          <PagePicker value={adding} pages={pages?.filter((p) => !list.includes(p.key)) ?? null} slot={field === "workbench" ? "workbench" : undefined} className="w-72" onChange={setAdding} />
          <Button size="sm" variant="outline" disabled={!adding} onClick={() => { put([...list, adding]); setAdding(""); }}><Plus /> {t("admin:common.add")}</Button>
        </div>
      ) : null}
    </CardContent></Card>
  );
}

// ---------------------------------------------------------------- use cases

export function UseCasesSection({ m, set, pages, readOnly, playbooks }: SectionProps & { playbooks: BuilderRef[] }) {
  const { t } = useTranslation();
  const { locale } = useLocale();
  const list = m.useCases;
  const put = (useCases: SpaceUseCase[]) => set({ useCases });
  const startOf = (u: SpaceUseCase) => (u.playbook ? "playbook" : u.page ? "page" : "prompt");
  return (
    <Card><CardContent className="pt-5">
      <SectionTitle icon={<Rocket className="size-4 text-aegold-700" />} title={t("admin:spaceDesigner.useCases.title")} hint={t("admin:spaceDesigner.useCases.hint")}
        actions={!readOnly ? <Button size="sm" variant="outline" onClick={() => put([...list, { key: `use-case-${list.length + 1}`, title: { en: "" }, prompt: "", status: "ready" }])}><Plus /> {t("admin:spaceDesigner.useCases.add")}</Button> : null} />
      {list.length === 0 ? <p className="text-sm text-muted-foreground">{t("admin:spaceDesigner.useCases.empty")}</p> : (
        <ol className="space-y-2.5">
          {list.map((u, i) => {
            const start = startOf(u);
            const patch = (p: Partial<SpaceUseCase>) => put(replace(list, i, { ...u, ...p }));
            return (
              <li key={i} className={cn("rounded-xl border bg-white p-3", u.status === "proposed" ? "border-dashed border-sand-300" : "border-sand-200")}>
                <div className="flex flex-wrap items-center gap-1.5">
                  <Input value={u.key} readOnly={readOnly} dir="ltr" className="h-8 w-48 font-mono" onChange={(e) => patch({ key: e.target.value })} aria-label={t("admin:common.key")} />
                  <NativeSelect value={u.status ?? "ready"} disabled={readOnly} className="h-8 w-36" onChange={(e) => patch({ status: e.target.value })} aria-label={t("admin:common.status")}>
                    <option value="ready">{t("admin:spaceDesigner.useCases.ready")}</option>
                    <option value="proposed">{t("admin:spaceDesigner.useCases.proposed")}</option>
                  </NativeSelect>
                  <Input value={u.icon ?? ""} readOnly={readOnly} dir="ltr" className="h-8 w-32 font-mono" placeholder={t("admin:spaceDesigner.nav.icon")} onChange={(e) => patch({ icon: e.target.value || null })} />
                  <Input value={u.duration ?? ""} readOnly={readOnly} className="h-8 w-32" placeholder={t("admin:spaceDesigner.useCases.duration")} onChange={(e) => patch({ duration: e.target.value || null })} />
                  <span className="ms-auto"><RowActions index={i} count={list.length} disabled={readOnly} onMove={(by) => put(move(list, i, by))} onRemove={() => put(without(list, i))} /></span>
                </div>
                <TextPair className="mt-2" value={u.title} readOnly={readOnly} placeholder={t("admin:spaceDesigner.useCases.titlePlaceholder")} onChange={(title) => patch({ title: title ?? { en: "" } })} />
                <TextPair className="mt-1.5" value={u.description} readOnly={readOnly} placeholder={t("admin:spaceDesigner.useCases.descriptionPlaceholder")} onChange={(description) => patch({ description })} />
                <TextPair className="mt-1.5" value={u.produces} readOnly={readOnly} placeholder={t("admin:spaceDesigner.useCases.producesPlaceholder")} onChange={(produces) => patch({ produces })} />
                <div className="mt-2 flex flex-wrap items-start gap-1.5">
                  <NativeSelect value={start} disabled={readOnly} className="h-8 w-60" aria-label={t("admin:spaceDesigner.useCases.start")}
                    onChange={(e) => patch({ playbook: e.target.value === "playbook" ? playbooks[0]?.key ?? "" : null, page: e.target.value === "page" ? "" : null, prompt: e.target.value === "prompt" ? u.prompt ?? "" : null })}>
                    <option value="playbook">{t("admin:spaceDesigner.useCases.startPlaybook")}</option>
                    <option value="page">{t("admin:spaceDesigner.useCases.startPage")}</option>
                    <option value="prompt">{t("admin:spaceDesigner.useCases.startPrompt")}</option>
                  </NativeSelect>
                  {start === "playbook" ? (
                    <NativeSelect value={u.playbook ?? ""} disabled={readOnly} className="h-8 min-w-56 flex-1" onChange={(e) => patch({ playbook: e.target.value })}>
                      {playbooks.map((p) => <option key={p.key} value={p.key}>{(locale === "ar" && p.nameAr) || p.name}</option>)}
                    </NativeSelect>
                  ) : start === "page" ? (
                    <PagePicker value={u.page} pages={pages} readOnly={readOnly} className="min-w-56 flex-1" onChange={(page) => patch({ page })} />
                  ) : (
                    <Textarea value={u.prompt ?? ""} readOnly={readOnly} className="min-h-[56px] min-w-56 flex-1" placeholder={t("admin:spaceDesigner.useCases.promptPlaceholder")} onChange={(e) => patch({ prompt: e.target.value })} />
                  )}
                </div>
                {start !== "prompt" && u.prompt ? <Textarea value={u.prompt} readOnly={readOnly} className="mt-1.5 min-h-[48px]" placeholder={t("admin:spaceDesigner.useCases.promptPlaceholder")} onChange={(e) => patch({ prompt: e.target.value || null })} /> : null}
                <ListInput className="mt-1.5" value={u.tags} readOnly={readOnly} placeholder={t("admin:spaceDesigner.useCases.tags")} onChange={(tags) => patch({ tags: tags.length ? tags : null })} />
              </li>
            );
          })}
        </ol>
      )}
    </CardContent></Card>
  );
}

// ---------------------------------------------------------------- grants

const GRANT_HINTS = ["outreach.campaigns", "outreach.*", "connectors:*", "agents:*", "data.import", "collections.write", "outputs.publish", "platform.api.write", "models.run", "sources.run"];

export function GrantsSection({ m, set, pages, readOnly }: SectionProps) {
  const { t } = useTranslation();
  const entries = Object.entries(m.grants);
  const put = (grants: Record<string, string[]>) => set({ grants });
  const [page, setPage] = useState("");
  const declared = (key: string) => pages?.find((p) => p.key === key) ?? null;
  return (
    <Card><CardContent className="pt-5">
      <SectionTitle icon={<KeyRound className="size-4 text-aegold-700" />} title={t("admin:spaceDesigner.grants.title")} hint={t("admin:spaceDesigner.grants.hint")} />
      <datalist id="space-grants">{GRANT_HINTS.map((g) => <option key={g} value={g} />)}</datalist>
      {entries.length === 0 ? <p className="text-sm text-muted-foreground">{t("admin:spaceDesigner.grants.empty")}</p> : (
        <ul className="space-y-2">
          {entries.map(([key, list]) => (
            <li key={key} className="rounded-lg border border-sand-200 bg-white p-2.5">
              <div className="flex items-center gap-2">
                <code className="min-w-0 flex-1 truncate font-mono text-sm" dir="ltr">{key}</code>
                {declared(key) ? null : <Badge size="sm" variant="warning">{t("admin:spaceDesigner.missingPage")}</Badge>}
                {!readOnly ? <Button size="sm" variant="ghost" className="text-danger-600" onClick={() => { const next = { ...m.grants }; delete next[key]; put(next); }}>{t("admin:common.remove")}</Button> : null}
              </div>
              <Input list="space-grants" value={list.join(", ")} readOnly={readOnly} dir="ltr" className="mt-1.5 h-8 font-mono" placeholder={t("admin:spaceDesigner.grants.placeholder")}
                onChange={(e) => put({ ...m.grants, [key]: e.target.value.split(",").map((s) => s.trim()).filter(Boolean) })} />
            </li>
          ))}
        </ul>
      )}
      {!readOnly ? (
        <div className="mt-2 flex gap-1.5">
          <PagePicker value={page} pages={pages?.filter((p) => !(p.key in m.grants)) ?? null} className="w-72" onChange={setPage} />
          <Button size="sm" variant="outline" disabled={!page} onClick={() => { put({ ...m.grants, [page]: [] }); setPage(""); }}><Plus /> {t("admin:spaceDesigner.grants.add")}</Button>
        </div>
      ) : null}
    </CardContent></Card>
  );
}
